'use client';

import { Box } from '@mui/material';
import type * as layout from '@ocodelib/compose-layout';
import { createColorVariables } from '@repo-foundation/util/mui-theme-generate-colors';
import type { IProjectLoader, ProjectFile } from '@repo/project-interface';
import type { IAppConfig, PyBasicAppType } from '@repo/py-basic-app';
import { PLUGIN_KEYS } from '@repo/py-basic-app';
import dynamic from 'next/dynamic';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { config } from '../../config';
import { CUSTOM_PALETTES, PY_PALETTE } from '../../theme/custom-palette';
import { WorkspaceTopbar } from './components/WorkspaceTopbar';

const PyBasicAppUi = dynamic(() => import('@repo/py-basic-app').then((m) => m.PyBasicAppUi), {
  ssr: false,
});

interface Props {
  previewMode?: boolean;
  onLoadApp?: (app: PyBasicAppType | null) => void;
  newProjectLoader?: IProjectLoader<ProjectFile[]>;
  initialProjectLoader?: IProjectLoader<ProjectFile[]>;
}

export function PyBasicAppLoader(props: Props) {
  const { onLoadApp, previewMode = false, newProjectLoader, initialProjectLoader } = props;
  const style = useMemo(
    () =>
      ({
        ...createColorVariables('primary', PY_PALETTE),
        ...createColorVariables('secondary', CUSTOM_PALETTES.cyan),
      }) as CSSProperties,
    [],
  );
  const appConfig = useMemo<IAppConfig>(
    () => ({
      apiBaseURL: config.apiBaseURL,
      datiBaseURL: config.datiBaseURL,
      previewMode,
      newProjectLoader,
      initialProjectLoader,
    }),
    [previewMode, newProjectLoader, initialProjectLoader],
  );

  return (
    <Box style={style}>
      <PyBasicAppUi
        onLayoutBuild={previewMode ? undefined : overrideLayout}
        appConfig={appConfig}
        onLoadApp={onLoadApp}
      />
    </Box>
  );
}

function overrideLayout(layoutManager: layout.ComposeLayoutManager) {
  function reg(key: string, component: React.ReactElement) {
    return layoutManager.registerPlugin(key, component);
  }
  reg(PLUGIN_KEYS.root.topBar, <WorkspaceTopbar />);
}
